import { onlineManager, useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { CheckIcon, TrashIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { startOAuthConnect, type User } from 'sdk';
import { appConfig, type EnabledOAuthProvider } from 'shared';
import { mapOAuthProviders } from '~/modules/auth/oauth-providers';
import { withStepUp } from '~/modules/auth/step-up';
import type { CallbackArgs } from '~/modules/common/data-table/types';
import { useDialoger } from '~/modules/common/dialoger/use-dialoger';
import { HelpText } from '~/modules/common/help-text';
import { toaster } from '~/modules/common/toaster/toaster';
import { ToolCard } from '~/modules/common/tool-card';
import { ConnectedAppsList } from '~/modules/me/connected-apps-list';
import { DeleteSelf } from '~/modules/me/delete-self';
import { MfaSwitch } from '~/modules/me/mfa/switch';
import { PasskeysList } from '~/modules/me/passkeys/list';
import { meAuthQueryOptions } from '~/modules/me/query';
import { SessionsList } from '~/modules/me/sessions-list';
import { Totp } from '~/modules/me/totp';
import { Badge } from '~/modules/ui/badge';
import { Button } from '~/modules/ui/button';
import { UpdateUserForm } from '~/modules/user/update-user-form';
import { useCurrentUser } from '~/modules/user/user-store';
import { cn } from '~/utils/cn';
import { tw } from '~/utils/tw';

const enabledStrategies = appConfig.enabledAuthStrategies;

const cardClass = tw('mx-auto sm:w-full');

export function AccountGeneralCard() {
  const user = useCurrentUser();

  return (
    <ToolCard label="c:general" unsaved id="update-user" className={cardClass}>
      <UpdateUserForm user={user} />
    </ToolCard>
  );
}

export function AccountSessionsCard() {
  const { t } = useTranslation();

  return (
    <ToolCard label="c:sessions" description={t('c:sessions.text')} className={cardClass}>
      <SessionsList />
    </ToolCard>
  );
}

export function AccountConnectedAppsCard() {
  const { t } = useTranslation();

  return (
    <ToolCard label="c:connected_apps" description={t('c:connected_apps.text')} className={cardClass}>
      <ConnectedAppsList />
    </ToolCard>
  );
}

export function AccountAuthenticationCard() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const { data: authData } = useSuspenseQuery(meAuthQueryOptions());
  const { enabledOAuth } = authData;

  const [loadingProvider, setLoadingProvider] = useState<EnabledOAuthProvider | null>(null);

  // The backend pins the provider's callback to this account first; the browser then leaves for the provider.
  const { mutate: connectProvider } = useMutation({
    mutationFn: async (_provider: EnabledOAuthProvider) => {
      await withStepUp(() => startOAuthConnect());
    },
    onMutate: (provider) => setLoadingProvider(provider),
    onSuccess: (_data, provider) => {
      const params = new URLSearchParams({ type: 'connect', redirectAfter: window.location.pathname + window.location.hash });
      window.location.assign(`${appConfig.backendAuthUrl}/${provider}?${params.toString()}`);
    },
    onError: () => setLoadingProvider(null),
  });

  const authenticateWithProvider = (provider: EnabledOAuthProvider) => {
    if (!onlineManager.isOnline()) return toaster.warning(t('c:action.offline.text'));
    connectProvider(provider);
  };

  return (
    <ToolCard label="c:authentication" description={t('c:authentication.text')} className={cardClass}>
      <div className="text-sm">
        {
          /* MFA */
          enabledStrategies.includes('passkey') && enabledStrategies.includes('totp') && (
            <>
              <HelpText content={t('c:mfa.text')}>
                <div className="flex items-center">
                  <p className="font-semibold">{t('c:mfa')}</p>
                  {!user.mfaRequired && (
                    <Badge size="xs" variant="outline" className="ml-2 border-success text-success max-sm:hidden">
                      {t('c:recommended')}
                    </Badge>
                  )}
                </div>
              </HelpText>
              <MfaSwitch />
            </>
          )
        }
        {
          /* Passkeys */
          enabledStrategies.includes('passkey') && (
            <>
              <HelpText content={t('c:passkey.text')}>
                <p className="font-semibold">{t('c:passkeys')}</p>
              </HelpText>
              <PasskeysList />
            </>
          )
        }

        {
          /* TOTP */
          enabledStrategies.includes('totp') && (
            <>
              <HelpText content={t('c:totp.text')}>
                <p className="font-semibold">{t('c:totp')}</p>
              </HelpText>
              <Totp />
            </>
          )
        }

        {
          /* OAuth */
          enabledStrategies.includes('oauth') && (
            <>
              <HelpText content={t('c:oauth.text')}>
                <p className="font-semibold">{t('c:oauth')}</p>
              </HelpText>

              <div className="mb-6 flex flex-col gap-3 sm:items-start">
                {appConfig.enabledOAuthProviders.map((id) => {
                  const provider = mapOAuthProviders.find((provider) => provider.id === id);
                  if (!provider) return null;
                  if (enabledOAuth.includes(id)) {
                    return (
                      <div key={provider.id} className="flex items-center justify-center gap-2 px-3 py-2">
                        <img
                          src={`/static/auth/${provider.id}-icon.svg`}
                          alt={provider.id}
                          className={cn('mr-2 size-4', provider.id === 'github' && 'dark:invert')}
                          loading="lazy"
                        />
                        <CheckIcon strokeWidth={3} className="size-4.5 text-success" />
                        {`${t('c:already_connected_to')} ${provider.name}`}
                      </div>
                    );
                  }
                  return (
                    // Assert is necessary because apps might not have all providers enabled
                    <Button
                      loading={loadingProvider === provider.id}
                      key={provider.id}
                      type="button"
                      variant="plain"
                      onClick={() => authenticateWithProvider(provider.id as EnabledOAuthProvider)}
                    >
                      <img
                        src={`/static/auth/${provider.id}-icon.svg`}
                        alt={provider.id}
                        className={cn('size-4', provider.id === 'github' && 'dark:invert')}
                        loading="lazy"
                      />
                      {`${t('c:add')} ${provider.name} ${t('c:account').toLowerCase()}`}
                    </Button>
                  );
                })}
              </div>
            </>
          )
        }
      </div>
    </ToolCard>
  );
}

export function AccountDeleteCard() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const deleteButtonRef = useRef(null);

  const openDeleteDialog = () => {
    useDialoger.getState().create(
      <DeleteSelf
        dialog
        callback={({ status }: CallbackArgs<User>) => {
          if (status === 'success') toaster.success(t('c:success.delete_resource', { resource: t('c:account') }));
        }}
      />,
      {
        id: 'delete-account',
        triggerRef: deleteButtonRef,
        className: 'md:max-w-xl',
        title: t('c:delete_account'),
        description: t('c:confirm.delete_account', { email: user.email, appName: appConfig.name }),
      },
    );
  };

  return (
    <ToolCard label="c:delete_account" description={t('c:delete_account.text', { appName: appConfig.name })} className={cardClass}>
      <Button ref={deleteButtonRef} variant="destructive" className="w-full sm:w-auto" onClick={openDeleteDialog}>
        <TrashIcon />
        {t('c:delete_account')}
      </Button>
    </ToolCard>
  );
}
